<template>
  <div class="contact-photo-field" :style="{ height: `${size}rem`, width: `${size}rem` }">
    <div @click="handleAvatarClick()" :class="{ clickable: isClickable }" class="photo-holder">
      <img v-if="photoUrl" :src="photoUrl" class="photo" alt="Foto do contato" />

      <avatar
        v-else
        :avatarLetter="avatarLetter"
        :size="size"
        :hasBorder="true"
        :hasStatus="false"
        avatarColor="#046AF3"
      />

      <div v-if="!isReadOnly" class="photo-overlay">
        <b-spinner v-if="isReading" small />

        <template v-else>
          <div
            v-if="!hasPlaceholderIcon"
            @click.stop="openFilePicker()"
            :title="photoUrl ? 'Trocar foto' : 'Adicionar foto'"
            class="overlay-action"
          >
            <Icon :width="iconSize" :height="iconSize" icon="mdi:camera-outline" />
          </div>

          <div v-if="photoUrl" @click.stop="removePhoto()" class="overlay-action remove" title="Remover foto">
            <Icon :width="iconSize" :height="iconSize" icon="mdi:trash-can-outline" />
          </div>
        </template>
      </div>

      <Icon
        v-if="hasPlaceholderIcon"
        :width="placeholderIconSize"
        :height="placeholderIconSize"
        icon="mdi:camera-outline"
        class="placeholder-icon"
      />
    </div>

    <input
      v-if="!isReadOnly"
      @change="handleSelection($event)"
      ref="fileInput"
      :accept="acceptAttribute"
      type="file"
      class="hidden-input"
    />

    <div v-if="isPreviewOpen" @click="closePreview()" class="photo-preview">
      <img :src="photoUrl" class="preview-image" alt="Foto do contato" />
    </div>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import { Icon } from "@iconify/vue2";

import Avatar from "@/views/components/Avatar.vue";
import {
  PHOTO_ACCEPT_ATTRIBUTE,
  PHOTO_MIME_TYPES,
  readUploadAsBase64,
  validateUpload,
} from "@/services/uploadValidationService.js";

export default {
  name: "ContactPhotoField",

  components: {
    Avatar,
    BSpinner,
    Icon,
  },

  props: {
    value: {
      type: Object,
      required: false,
      default: null,
    },

    avatarLetter: {
      type: String,
      required: false,
      default: "S",
    },

    size: {
      type: Number,
      required: false,
      default: 3,
    },

    isReadOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      acceptAttribute: PHOTO_ACCEPT_ATTRIBUTE,
      isPreviewOpen: false,
      isReading: false,
    };
  },

  computed: {
    photoUrl() {
      return this.value?.url || this.value?.base64File || null;
    },

    isClickable() {
      return Boolean(this.photoUrl) || !this.isReadOnly;
    },

    iconSize() {
      return Math.round(this.size * 4.5);
    },

    hasPlaceholderIcon() {
      return !this.photoUrl && !this.avatarLetter;
    },

    placeholderIconSize() {
      return Math.round(this.size * 6);
    },
  },

  methods: {
    openFilePicker() {
      this.$refs.fileInput.click();
    },

    openPreview() {
      if (!this.photoUrl) return;

      this.isPreviewOpen = true;
    },

    closePreview() {
      this.isPreviewOpen = false;
    },

    removePhoto() {
      this.closePreview();

      this.$emit("input", null);
    },

    handleAvatarClick() {
      if (this.photoUrl) {
        this.openPreview();

        return;
      }

      if (this.isReadOnly) return;

      this.openFilePicker();
    },

    showValidationError(reason) {
      this.$bvToast.toast(reason, {
        title: "Arquivo não suportado",
        autoHideDelay: 5000,
        variant: "danger",
        toaster: "b-toaster-top-left",
        solid: true,
      });
    },

    async handleSelection(event) {
      const file = event.target.files[0];

      event.target.value = "";

      if (!file) return;

      const validation = validateUpload(file, PHOTO_MIME_TYPES);

      if (!validation.valid) {
        this.showValidationError(validation.reason);

        return;
      }

      this.isReading = true;

      try {
        const photo = await readUploadAsBase64(file);

        this.$emit("input", photo);
      } catch (error) {
        this.showValidationError(`Não foi possível ler o arquivo "${file.name}".`);
      } finally {
        this.isReading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-photo-field {
  position: relative;
  flex-shrink: 0;

  ::v-deep .avatar-container {
    height: 100% !important;
    width: 100% !important;
    margin-right: 0;
  }

  .photo-holder {
    position: relative;
    height: 100%;
    width: 100%;

    &.clickable {
      cursor: pointer;
    }

    .photo {
      display: block;
      height: 100%;
      width: 100%;
      border: 1px solid #046af3;
      border-radius: 100%;
      object-fit: cover;
    }

    .placeholder-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #046af3;
      pointer-events: none;
      transition: color 0.15s ease;
    }

    &.clickable:hover .placeholder-icon {
      color: #ffffff;
    }

    .photo-overlay {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      gap: 6px;
      border-radius: 100%;
      background: #00000073;
      color: #ffffff;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s ease;

      .overlay-action {
        display: flex;
        align-items: center;
        cursor: pointer;

        &.remove {
          color: #ff8a8a;
        }
      }
    }

    &:hover .photo-overlay {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .hidden-input {
    display: none;
  }

  .photo-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 20px;
    background: #00000099;
    cursor: zoom-out;
    z-index: 1060;

    .preview-image {
      max-height: 90%;
      max-width: 90%;
      border-radius: 8px;
      object-fit: contain;
    }
  }
}
</style>
